import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Theme, createStyles, makeStyles } from '@bb-ui/react-library/dist/components/styles';
import { DefaultButton, PrimaryButton } from '@bb-ui/react-library/dist/components/Button';
import { CardHeader } from '@bb-ui/react-library/dist/components/CardHeader';
import { CardContent } from '@bb-ui/react-library/dist/components/CardContent';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { List } from '@bb-ui/react-library/dist/components/List';
import { ListItem } from '@bb-ui/react-library/dist/components/ListItem';
import { ListItemText } from '@bb-ui/react-library/dist/components/ListItemText';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { Edit } from '@bb-ui/icons/dist/small/Edit';
import { Lock } from '@bb-ui/icons/dist/small/Lock';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { useTenantContext } from 'contexts/TenantContext';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PageCard } from 'components/PageCard';
import { Link } from 'react-router-dom';
import { IdentityProvider, PublicAuth0BrokerData } from '../TenantIdp.types';
import { useRestApi } from 'hooks/useRestApi';
import { useSnackbar } from 'hooks/useSnackbar';
import { apiUrl } from '../../../utils/apiUrl';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardContent: {
      padding: theme.spacing(2, 0, 0, 0),
    },
    cardHeader: {
      padding: theme.spacing(0, 0, 4, 0),
      borderBottom: `1px solid ${theme.palette.border.main}`,
    },
    cardHeaderSecondary: {
      padding: theme.spacing(0, 0, 3, 0),
    },
    cardHeaderAction: {
      marginRight: 0,
    },
    cardHeaderActionControl: {
      marginLeft: theme.spacing(1.5),
    },
    listText: {
      paddingLeft: 0,
    },
    listTextPrimary: {
      marginBottom: theme.spacing(1),
    },
  }),
);

export interface IdpInfoCardProps {
  tenantId: string;
  idp?: IdentityProvider;
  onUpdate?: () => void;
}

export const IdpSettingsCard: React.FunctionComponent<IdpInfoCardProps> = (props) => {
  const { tenantId, idp } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();
  const { supportAuth0 } = useAppConfigContext();
  const { tenant } = useTenantContext();
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [newIdpDisplayName, setNewIdpDisplayName] = React.useState('');

  const isCloned = !!(idp?.clonedFromTenantId && idp?.clonedFromIdpId);

  const samlMetadataUrl = React.useMemo(() => {
    if (idp?.type !== 'SAML') {
      return null;
    }
    const tenantRegion = tenant?.region;
    const spDomain = tenantRegion && supportAuth0?.regions?.[tenantRegion]?.domain;
    const spId = (idp?.brokerData as PublicAuth0BrokerData)?.identityProviderName;
    return spDomain && spId && `https://${spDomain}/samlp/metadata?connection=${spId}`;
  }, [tenant, supportAuth0, idp]);

  const { enqueueSnackbar } = useSnackbar();

  const {
    clearFailedRequests,
    clearSucceededRequests,
    doPatch,
    failedRequests,
    loadingRequests,
    succeededRequests,
  } = useRestApi(apiUrl('sso/admin', `tenants/${tenantId}/identityProviders`), {
    manual: true,
  });

  React.useEffect(() => {
    if (idp) {
      setNewIdpDisplayName(idp.displayName || '');
    }
  }, [idp]);

  React.useEffect(() => {
    if (succeededRequests.length > 0) {
      enqueueSnackbar(t('tenantIdp.updateSuccess'), { variant: 'success' });
      clearSucceededRequests();
      props.onUpdate?.();
    }
  }, [succeededRequests, clearSucceededRequests, enqueueSnackbar, t, props]);

  React.useEffect(() => {
    if (failedRequests.length > 0) {
      enqueueSnackbar(t('tenantIdp.updateError'), { variant: 'error' });
      clearFailedRequests();
      setIsEditOpen(true);
    }
  }, [failedRequests, clearFailedRequests, enqueueSnackbar, t]);

  function onEditIdp() {
    setIsEditOpen(true);
  }

  function onCancelEdit() {
    setIsEditOpen(false);
    setNewIdpDisplayName(idp?.displayName || '');
  }

  function onSaveDisplayName() {
    doPatch(`${idp?.id}`, { displayName: newIdpDisplayName });
    setIsEditOpen(false);
  }

  function renderCardAction() {
    if (isCloned) {
      return (
        <Tooltip title={t('tenantIdp.clonedReadOnlyTooltip')}>
          <IconButton className={classes.cardHeaderActionControl} aria-label={t('global.readOnly')}>
            <Lock />
            {t('global.readOnly')}
          </IconButton>
        </Tooltip>
      );
    }
    if (isEditOpen) {
      return (
        <>
          <DefaultButton onClick={onCancelEdit} data-testid="cancel-idp-edit">
            {t('global.cancel')}
          </DefaultButton>
          <PrimaryButton
            onClick={onSaveDisplayName}
            className={classes.cardHeaderActionControl}
            data-testid="save-idp-edit"
          >
            {t('global.save')}
          </PrimaryButton>
        </>
      );
    }
    return (
      <Tooltip title={t('tenantIdp.editPropagateTooltip')}>
        <span>
          <IconButton
            className={classes.cardHeaderActionControl}
            onClick={onEditIdp}
            aria-label={t('global.edit')}
          >
            <Edit />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <PageCard data-testid="idp-settings-card">
      <CardHeader
        className={classes.cardHeaderSecondary}
        classes={{ action: classes.cardHeaderAction }}
        title={t('tenantIdp.idpSettings')}
        titleTypographyProps={{ component: 'h3', variant: 'h2' } as any} // any: component expects 'span' | undefined
        action={renderCardAction()}
      />
      {loadingRequests ? (
        <LoadingIndicator data-testid="loading-idp-settings" />
      ) : (
        <CardContent className={classes.cardContent}>
          <List>
            <ListItem disableGutters>
              {isEditOpen && !isCloned ? (
                <TextField
                  data-testid="idp-display-name-input"
                  label={t('tenantIdp.idpDisplayName')}
                  value={newIdpDisplayName}
                  onChange={(e) => setNewIdpDisplayName(e.target.value)}
                  fullWidth
                />
              ) : (
                <ListItemText
                  className={classes.listText}
                  classes={{ primary: classes.listTextPrimary }}
                  primary={t('tenantIdp.idpDisplayName')}
                  secondary={newIdpDisplayName || <i>{t('global.unset')}</i>}
                />
              )}
            </ListItem>
            {isCloned && (
              <ListItem disableGutters>
                <ListItemText
                  className={classes.listText}
                  classes={{ primary: classes.listTextPrimary }}
                  primary={t('idpCard.clonedFrom', 'Cloned from')}
                  secondary={
                    <Link to={`/tenants/${idp?.clonedFromTenantId}/auth/${idp?.clonedFromIdpId}`}>
                      {`${idp.clonedFromTenantId} / ${idp?.clonedFromIdpId}`}
                    </Link>
                  }
                />
              </ListItem>
            )}
            {samlMetadataUrl && (
              <ListItem disableGutters>
                <ListItemText
                  className={classes.listText}
                  classes={{ primary: classes.listTextPrimary }}
                  primary={t('tenantIdp.samlSpMetadata')}
                  secondary={
                    <a href={samlMetadataUrl} target="_blank" rel="noopener noreferrer">
                      {samlMetadataUrl}
                    </a>
                  }
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      )}
    </PageCard>
  );
};
